import type { ErrorCode } from '@nw/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network_error',
    message: string,
    readonly details?: unknown,
    readonly correlationId?: string,
  ) {
    super(message);
  }
}

let csrfToken = '';
let onUnauthenticated: (() => void) | null = null;
export function setCsrfToken(token: string) {
  csrfToken = token;
}
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

export interface ApiResult<T, M = Record<string, unknown>> {
  data: T;
  meta?: M;
}

export async function api<T, M = Record<string, unknown>>(
  path: string,
  init: {
    method?: string;
    body?: unknown;
    form?: FormData;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {},
): Promise<ApiResult<T, M>> {
  const method = init.method ?? (init.body !== undefined || init.form ? 'POST' : 'GET');
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...init.headers,
  };
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken;
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: init.form ?? (init.body !== undefined ? JSON.stringify(init.body) : undefined),
      signal: init.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', 'The workspace server could not be reached. Check your connection.');
  }
  const text = await res.text();
  const json = text
    ? (JSON.parse(text) as {
        data?: T;
        meta?: M;
        error?: {
          code: ErrorCode;
          message: string;
          details?: unknown;
          correlationId?: string;
        };
      })
    : {};
  if (!res.ok || json.error) {
    const e = json.error;
    if (res.status === 401 && onUnauthenticated) onUnauthenticated();
    throw new ApiError(res.status, e?.code ?? 'internal_error', e?.message ?? `Request failed (${res.status})`, e?.details, e?.correlationId);
  }
  return { data: json.data as T, meta: json.meta };
}

export function newIdempotencyKey() {
  return `idk_${crypto.randomUUID().replace(/-/g, '')}`;
}
